import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { QrCode } from "lucide-react";
import { Spinner } from "../ui/Spinner";
import { appOpenPath, type OpenKind } from "../../lib/appLinks";

interface MobileQrCodeProps {
	url: string;
	size: number;
	alt: string;
	className?: string;
	centerLogoSrc?: string;
}

/**
 * URL a phone scans. With a target it mints the canonical /open/<kind>/<value> deep link, so a
 * scanner with the app installed lands in the app (App Link / universal link) instead of the site;
 * without one — the generic "play on mobile" prompts — it opens the smart store-download page.
 */
export function mobilePlayUrl(target?: { kind: OpenKind; value: string }): string {
	if (typeof window === "undefined") return "";
	if (target) return `${window.location.origin}${appOpenPath(target.kind, target.value)}`;
	return new URL("/store-download", window.location.href).toString();
}

export function MobileQrCode({
	url,
	size,
	alt,
	className = "",
	centerLogoSrc,
}: MobileQrCodeProps) {
	const [dataUrl, setDataUrl] = useState("");
	const [failed, setFailed] = useState(false);

	useEffect(() => {
		let active = true;
		setDataUrl("");
		setFailed(false);
		if (!url) return;

		void QRCode.toDataURL(url, {
			width: size * 2,
			margin: 1,
			errorCorrectionLevel: centerLogoSrc ? "H" : "M",
			color: { dark: "#08060c", light: "#ffffff" },
		}).then(
			(result) => {
				if (active) setDataUrl(result);
			},
			() => {
				if (active) setFailed(true);
			},
		);

		return () => {
			active = false;
		};
	}, [centerLogoSrc, size, url]);

	const logoBoxSize = Math.round(size * 0.24);
	const logoSize = Math.round(size * 0.2);

	return (
		<div
			className={`relative grid shrink-0 place-items-center overflow-hidden bg-white ${className}`}
			style={{ width: size, height: size }}
			role={failed ? "img" : undefined}
			aria-label={failed ? alt : undefined}
		>
			{dataUrl ? (
				<>
					<img src={dataUrl} width={size} height={size} alt={alt} className="size-full" />
					{centerLogoSrc ? (
						<span
							aria-hidden="true"
							className="absolute grid place-items-center rounded-[22%] bg-white"
							style={{ width: logoBoxSize, height: logoBoxSize }}
						>
							<img src={centerLogoSrc} width={logoSize} height={logoSize} alt="" />
						</span>
					) : null}
				</>
			) : failed ? (
				<QrCode size={Math.max(24, Math.round(size * 0.45))} className="text-black/45" />
			) : (
				<Spinner size={Math.max(16, Math.round(size * 0.16))} />
			)}
		</div>
	);
}
